// client/src/pages/Admin/ProjectManager/hooks/useBulkSelection.ts
/**
 * USE BULK SELECTION HOOK
 * 
 * This hook manages bulk selection of items with type safety and performance optimizations.
 * It follows international NGO standards for accessibility and user experience.
 * 
 * @module ProjectManager/Hooks/useBulkSelection
 * 
 * @example
 * // Basic usage
 * const { selectedIds, toggleSelection, toggleSelectAll, clearSelection } = useBulkSelection(projects);
 * 
 * // Access selected items
 * const selectedProjects = projects.filter(p => p._id && selectedIds.has(p._id));
 */

import { useState, useCallback, useMemo } from 'react';

// ============================================
// TYPES
// ============================================

/**
 * Return type for the useBulkSelection hook
 */
interface UseBulkSelectionReturn<T extends { _id?: string }> {
    /** Set of selected item IDs */
    selectedIds: Set<string>;
    /** Whether all items in the current list are selected */
    isAllSelected: boolean;
    /** Number of selected items */
    selectedCount: number;
    /** Array of selected item IDs */
    selectedIdsList: string[];
    /** Toggle selection for a single item */
    toggleSelection: (id: string) => void;
    /** Toggle selection for all items in the provided list */
    toggleSelectAll: (items: T[]) => void;
    /** Clear all selections */
    clearSelection: () => void;
    /** Set selected IDs directly */
    setSelectedIds: (ids: Set<string>) => void;
    /** Get selected items from a list */
    getSelectedItems: <T extends { _id?: string }>(items: T[]) => T[];
    /** Get selection count for a specific list */
    getSelectionCount: (items: Array<{ _id?: string }>) => number;
    /** Check if a specific item is selected */
    isSelected: (id: string) => boolean;
}

// ============================================
// HOOK IMPLEMENTATION
// ============================================

/**
 * useBulkSelection hook for managing bulk selection of items
 * 
 * @param items - Array of items with optional _id field
 * @returns Bulk selection state and actions
 * 
 * @template T - Type of items with _id field
 */
export const useBulkSelection = <T extends { _id?: string }>(
    items: T[] = []
): UseBulkSelectionReturn<T> => {
    // ============================================
    // STATE
    // ============================================
    
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // ============================================
    // ACTIONS
    // ============================================
    
    /**
     * Toggle selection for a single item
     * @param id - ID of the item to toggle
     */
    const toggleSelection = useCallback((id: string): void => {
        if (!id) {
            console.warn('useBulkSelection: toggleSelection called with invalid ID');
            return;
        }
        
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    }, []);

    /**
     * Toggle selection for all items in the provided list
     * @param itemsList - Array of items to toggle selection for
     */
    const toggleSelectAll = useCallback((itemsList: T[]): void => {
        if (!Array.isArray(itemsList) || itemsList.length === 0) {
            return;
        }

        const validIds = itemsList
            .filter((item): item is T & { _id: string } => 
                item._id !== undefined && item._id !== null && item._id !== ''
            )
            .map(item => item._id);

        if (validIds.length === 0) {
            return;
        }

        setSelectedIds(prev => {
            const allSelected = validIds.every(id => prev.has(id));
            
            if (allSelected) {
                // Deselect all valid IDs
                const newSet = new Set(prev);
                validIds.forEach(id => newSet.delete(id));
                return newSet;
            } else {
                // Select all valid IDs
                const newSet = new Set(prev);
                validIds.forEach(id => newSet.add(id));
                return newSet;
            }
        });
    }, []);

    /**
     * Clear all selections
     */
    const clearSelection = useCallback((): void => {
        setSelectedIds(new Set());
    }, []);

    // ============================================
    // SELECTED ITEMS HELPERS
    // ============================================
    
    /**
     * Get selected items from a list
     * @param itemsList - List of items to filter
     * @returns Filtered list of selected items
     */
    const getSelectedItems = useCallback(<U extends { _id?: string }>(
        itemsList: U[]
    ): U[] => {
        if (!Array.isArray(itemsList) || selectedIds.size === 0) {
            return [];
        }
        return itemsList.filter(item => 
            item._id !== undefined && selectedIds.has(item._id)
        );
    }, [selectedIds]);

    /**
     * Get the number of selected items in a list
     * @param itemsList - List of items to count
     * @returns Number of selected items
     */
    const getSelectionCount = useCallback((itemsList: Array<{ _id?: string }>): number => {
        if (!Array.isArray(itemsList) || selectedIds.size === 0) {
            return 0;
        }
        return itemsList.filter(item => 
            item._id !== undefined && selectedIds.has(item._id)
        ).length;
    }, [selectedIds]);

    /**
     * Check if a specific item is selected
     * @param id - ID of the item to check
     * @returns True if the item is selected
     */
    const isSelected = useCallback((id: string): boolean => {
        if (!id) return false;
        return selectedIds.has(id);
    }, [selectedIds]);

    // ============================================
    // COMPUTED VALUES
    // ============================================
    
    /**
     * Check if all items in the current list are selected
     */
    const isAllSelected = useMemo((): boolean => {
        if (!Array.isArray(items) || items.length === 0) {
            return false;
        }
        
        const validItems = items.filter(
            (item): item is T & { _id: string } => 
                item._id !== undefined && item._id !== null && item._id !== ''
        );
        
        if (validItems.length === 0) {
            return false;
        }
        
        return validItems.every(item => 
            item._id !== undefined && selectedIds.has(item._id)
        );
    }, [items, selectedIds]);

    /**
     * Get the total number of selected items
     */
    const selectedCount = useMemo((): number => {
        return selectedIds.size;
    }, [selectedIds]);

    /**
     * Get the list of selected item IDs
     */
    const selectedIdsList = useMemo((): string[] => {
        return Array.from(selectedIds);
    }, [selectedIds]);

    // ============================================
    // RETURN
    // ============================================
    
    return {
        // State
        selectedIds,
        isAllSelected,
        selectedCount,
        selectedIdsList,
        
        // Actions
        toggleSelection,
        toggleSelectAll,
        clearSelection,
        setSelectedIds,
        
        // Helpers
        getSelectedItems,
        getSelectionCount,
        isSelected
    };
};

// ============================================
// DEFAULT EXPORT
// ============================================

export default useBulkSelection;